import { AnalyticsContext, useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import React, { createContext, useContext, useMemo } from 'react';
import { ThemeJson } from '../routes/CardCustomizer/CardCustomizer.types';
import { usePrimaryEventLocation } from '../routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { fillPageWithEventInfo } from '../utils/fillPageWithEventInfo';
import { useGetEventPrivacyForCardCustomizerQuery } from '@graphql/generated';

export type TemplateForAutofill = { id: string; filledAt?: string; themeJSON?: ThemeJson };

// Default behavior is for public shop where there is no associated event.
const AutofillContext = createContext({
  autofill: (_template: TemplateForAutofill, _trackEvent?: 'trackEvent') => {},
  loading: false,
});

export function AutofillProvider(props: { children: React.ReactNode }) {
  const { children } = props;

  const { track } = useContext(AnalyticsContext);
  const { t } = useTranslation('stationery');
  const tNumerals = t('numerals');

  const [isAutofillEnabled, featureLoading] = useIsFeatureEnabled('printGalleryAutofill');
  const { eventHandle, eventInfo, loading: eventInfoLoading } = useEventInfo();
  const { location, loading: locationLoading } = usePrimaryEventLocation();
  const { data: privacyData, loading: privacyDataLoading } = useGetEventPrivacyForCardCustomizerQuery({
    variables: {
      eventId: eventInfo?.eventId!,
    },
    batchMode: 'fast',
    skip: !eventInfo?.eventId,
  });
  const loading = featureLoading || eventInfoLoading || locationLoading || privacyDataLoading;

  const value = useMemo(() => {
    return {
      autofill: (template: TemplateForAutofill, trackEvent?: 'trackEvent') => {
        const privacyMode = privacyData?.eventById?.settings.privacyMode;

        if (!isAutofillEnabled) return;
        if (!template?.themeJSON) return;
        if (!privacyMode) return;
        if (loading) return;

        const { front, back } = template.themeJSON.card;
        // prettier-ignore
        const filledPatterns = [
          ...fillPageWithEventInfo(front, eventInfo, location, tNumerals, privacyMode),
          ...fillPageWithEventInfo(back, eventInfo, location, tNumerals, privacyMode)
        ];
        template.themeJSON.filledAt = new Date().toISOString();
        if (trackEvent === 'trackEvent') {
          track({
            category: 'messaging.card',
            action: 'Patterns Filled',
            extraInfo: {
              templateId: template.id,
              eventHandle,
              eventId: eventInfo?.eventId,
              filledPatterns,
            },
          });
        }
      },
      loading,
    };
  }, [eventHandle, eventInfo, isAutofillEnabled, location, loading, tNumerals, track, privacyData]);

  return <AutofillContext.Provider value={value}>{children}</AutofillContext.Provider>;
}

export function useAutofill() {
  return useContext(AutofillContext);
}
